import React, { useImperativeHandle, useRef, useState } from 'react';
import { ChevronRight } from '@development-nl/icons-library';
import { Box, Menu, MenuItem } from '@mui/material';
import PropTypes from 'prop-types';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme, { isOpen }) => ({
    root: {
        backgroundColor: isOpen ? theme.palette.action.hover : 'rgba(0,0,0,0)',
    },
}));

// This component is just usable in the @Obelix/uikit
const NestedMenuItem = React.forwardRef(
    (
        {
            isParentMenuOpen,
            label,
            rightIcon,
            children,
            className,
            tabIndex: tabIndexProp,
            disabled,
            ContainerProps: ContainerPropsProp,
            dataTestId,
            onClick,
            ...restProps
        },
        ref
    ) => {
        const { ref: containerRefProp, ...ContainerProps } = ContainerPropsProp;

        const menuItemRef = useRef(null);
        useImperativeHandle(ref, () => menuItemRef.current);

        const containerRef = useRef(null);
        useImperativeHandle(containerRefProp, () => containerRef.current);

        const menuContainerRef = useRef(null);

        const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);

        const handleMouseEnter = event => {
            setIsSubMenuOpen(true);

            if (ContainerProps && ContainerProps.onMouseEnter) {
                ContainerProps.onMouseEnter(event);
            }
        };

        const handleMouseLeave = event => {
            setIsSubMenuOpen(false);
            if (ContainerProps && ContainerProps.onMouseLeave) {
                ContainerProps.onMouseLeave(event);
            }
        };

        const isSubmenuFocused = () => {
            const active = containerRef.current?.ownerDocument?.activeElement;
            for (const child of menuContainerRef.current?.children || []) {
                if (child === active) {
                    return true;
                }
            }

            return false;
        };

        const handleFocus = event => {
            if (event.target === containerRef.current) {
                setIsSubMenuOpen(true);
            }
            const { onFocus } = ContainerProps || {};
            if (onFocus) {
                ContainerProps.onFocus(event);
            }
        };

        const handleKeyDown = event => {
            console.log('EVENT.KEY: ', event.key);
            if (event.key === 'Escape') {
                return;
            }

            if (isSubmenuFocused()) {
                event.stopPropagation();
            }

            const active = containerRef.current?.ownerDocument?.activeElement;

            if (event.key === 'ArrowLeft' && isSubmenuFocused()) {
                // eslint-disable-next-line no-unused-expressions
                containerRef?.current?.focus();
            }

            // You are able to navigate with arrow keys. If it is an ArrowRight, then you can jump to the sub menus
            if (event.key === 'ArrowRight' && event.target === containerRef.current && event.target === active) {
                const firstChild = menuContainerRef.current?.children[0] || undefined;

                if (firstChild) {
                    firstChild.focus();
                }
            }
        };

        const isOpen = isSubMenuOpen && isParentMenuOpen;
        const { classes, cx } = useStyles({ isOpen });

        // Root element must have a `tabIndex` attribute for keyboard navigation
        let tabIndex;
        if (!disabled) {
            tabIndex = tabIndexProp !== undefined ? tabIndexProp : -1;
        }

        return (
            <Box
                data-cy="nestedMenuItem"
                {...ContainerProps}
                ref={containerRef}
                tabIndex={tabIndex}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <MenuItem
                    {...restProps}
                    ref={menuItemRef}
                    className={cx(classes.root, className)}
                    data-testid={dataTestId}
                    onClick={onClick}
                >
                    {label}
                    {rightIcon}
                </MenuItem>
                <Menu
                    anchorEl={menuItemRef.current}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    autoFocus={false}
                    disableAutoFocus={true}
                    disableEnforceFocus={true}
                    open={isOpen}
                    style={{ pointerEvents: 'none' }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    onClose={() => {
                        setIsSubMenuOpen(false);
                    }}
                >
                    <Box ref={menuContainerRef} style={{ pointerEvents: 'auto' }}>
                        {children}
                    </Box>
                </Menu>
            </Box>
        );
    }
);

NestedMenuItem.defaultProps = {
    children: null,
    className: '',
    ContainerProps: {},
    disabled: false,
    rightIcon: <ChevronRight />,
    tabIndex: -1,
    dataTestId: '',
};

NestedMenuItem.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    ContainerProps: PropTypes.object,
    disabled: PropTypes.bool,
    isParentMenuOpen: PropTypes.bool.isRequired,
    label: PropTypes.node.isRequired,
    rightIcon: PropTypes.node,
    tabIndex: PropTypes.number,
    dataTestId: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
};

export { NestedMenuItem };
